/**
 * MCQ Element Editor
 * Supports multiple content types for question and choices
 */

import * as React from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Button,
    Checkbox,
    Label,
    RadioGroup,
    RadioGroupItem,
    Switch,
    Textarea,
    cn,
} from '@ai-content-hub/content-kit';
import type { BlockContent, BlockContentType } from '../block';
import type { ElementEditorProps } from '@ai-content-hub/content-kit';
import { Plus, Trash2, GripVertical, ChevronDown, ChevronUp } from 'lucide-react';
import {
    type MCQElementData,
    type MCQChoice,
    type MCQContent,
    type MCQContentType,
    createDefaultContent,
    normalizeMCQData,
    mcqElementSchema,
} from './schema';
import { BlockEditor } from '../block';
import { keepFirstCorrect, selectSingleCorrect } from './correctAnswer';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';

export interface MCQEditProps extends ElementEditorProps<MCQElementData> {
    /** Validation errors keyed by field path */
    errors?: Record<string, string>;
}

// Generate unique ID for choices
const generateId = () => Math.random().toString(36).substring(2, 8);

/**
 * EAA-1772: the MCQ Set question is always edited as a rich-text form, so authors
 * can embed audio / images / formatting inline WITH the question text (Moodle
 * imports rely on this to render an audio player + image inside the question).
 *
 * A legacy plain-text question is lifted into a rich-text block preserving its
 * text; an already rich-text question passes through unchanged; any other media
 * block (image/audio/video) is left as-is so its data is never dropped.
 */
const toRichTextQuestion = (question: MCQContent | undefined): MCQContent => {
    if (!question) return createDefaultContent('rich-text');
    if (question.type === 'rich-text' || question.type !== 'text') return question;
    const text = question.text ?? '';
    if (!text) return createDefaultContent('rich-text');
    return {
        type: 'rich-text',
        content: {
            type: 'doc',
            content: [{ type: 'paragraph', content: [{ type: 'text', text }] }],
        },
    };
};

interface ChoicesContainerProps {
    single: boolean;
    value: string;
    onValueChange: (id: string) => void;
    disabled: boolean;
    children: React.ReactNode;
}

/** Choice list body — a radio group when the question has one key, a plain list otherwise. */
const ChoicesContainer: React.FC<ChoicesContainerProps> = ({
    single,
    value,
    onValueChange,
    disabled,
    children,
}) => {
    if (!single) return <div className="mcq-edit-choices-body space-y-3">{children}</div>;
    return (
        <RadioGroup
            value={value}
            onValueChange={onValueChange}
            disabled={disabled}
            className="mcq-edit-choices-body mcq-edit-choices-body--single gap-3"
        >
            {children}
        </RadioGroup>
    );
};

export const MCQEdit: React.FC<MCQEditProps> = ({
    value,
    onChange,
    disabled = false,
    className,
    errors,
    config,
    schema: schemaProp,
    language,
}) => {
    const schema = schemaProp ?? mcqElementSchema;
    const { t } = useElementSchemaLocale(schema.i18n, { config, language });

    // Normalize data
    const data = React.useMemo(() => normalizeMCQData(value || {}), [value]);

    // EAA-1772: question is always a rich-text form (see toRichTextQuestion).
    const questionContent = React.useMemo<MCQContent>(
        () => toRichTextQuestion(data.question),
        [data.question]
    );

    // Update data
    const updateData = (updates: Partial<MCQElementData>) => {
        onChange({ ...data, ...updates });
    };

    // Update question content
    const handleQuestionContentChange = (content: BlockContent) => {
        updateData({ question: content as MCQContent });
    };

    // Add choice
    const addChoice = () => {
        if (disabled) return;
        const newChoice: MCQChoice = {
            id: generateId(),
            content: {
                type: 'text' as const,
                text: '',
                variant: 'body' as const,
                alignment: 'left' as const,
            },
            isCorrect: false,
        };
        updateData({ choices: [...data.choices, newChoice] });
    };

    // Remove choice
    const removeChoice = (id: string) => {
        if (disabled || data.choices.length <= 2) return;
        updateData({ choices: data.choices.filter((c) => c.id !== id) });
    };

    // Update choice
    const updateChoice = (id: string, updates: Partial<MCQChoice>) => {
        if (disabled) return;
        const newChoices = data.choices.map((c) => {
            if (c.id !== id) return c;
            return { ...c, ...updates };
        });
        updateData({ choices: newChoices });
    };

    // EAA-2030: single-choice marks its key with a radio — picking one clears the others.
    // Ticking a second checkbox used to silently flip the question to multiple-correct.
    const handleSingleCorrectChange = (id: string) => {
        if (disabled) return;
        updateData({ choices: selectSingleCorrect(data.choices, id) });
    };

    // Turning multiple-correct off keeps only the first key, so the radio has one value.
    const handleMultipleCorrectChange = (checked: boolean) => {
        if (checked) {
            updateData({ multipleCorrect: true });
            return;
        }
        updateData({ multipleCorrect: false, choices: keepFirstCorrect(data.choices) });
    };

    const singleCorrectId = data.choices.find((c) => c.isCorrect)?.id ?? '';

    // Check if at least one correct answer is selected
    const hasCorrectAnswer = data.choices.some((c) => c.isCorrect);
    const correctAnswerError =
        errors?.choices ??
        (!hasCorrectAnswer && data.choices.length >= 2 ? t('mcqSelectCorrectAnswer') : undefined);

    // Change choice content type
    const handleChoiceTypeChange = (id: string, type: BlockContentType) => {
        const choice = data.choices.find((c) => c.id === id);
        const currentType = choice?.content?.type || 'text';
        if (!choice || currentType === type) return;
        const newContent = createDefaultContent(type as MCQContentType);
        updateChoice(id, { content: newContent });
    };

    // Move choice up
    const moveUp = (index: number) => {
        if (disabled || index === 0) return;
        const newChoices = [...data.choices];
        [newChoices[index - 1], newChoices[index]] = [newChoices[index], newChoices[index - 1]];
        updateData({ choices: newChoices });
    };

    // Move choice down
    const moveDown = (index: number) => {
        if (disabled || index === data.choices.length - 1) return;
        const newChoices = [...data.choices];
        [newChoices[index], newChoices[index + 1]] = [newChoices[index + 1], newChoices[index]];
        updateData({ choices: newChoices });
    };

    return (
        <div className={cn('mcq-edit mcq-edit-root space-y-6', className)}>
            {/* Question Section */}

            <div className="mcq-edit-question-header pb-2">
                <CardTitle className="mcq-edit-question-title text-base">{t('question')}</CardTitle>
            </div>
            {/* EAA-1772: full rich-text editor (with toolbar) so the question can
                carry inline audio/images/formatting, not just a one-line input. */}
            <BlockEditor
                layout="default"
                content={questionContent}
                onChange={handleQuestionContentChange}
                disabled={disabled}
                className="mcq-edit-question-body pb-2"
            />

            {/* Choices Section */}
            <div className="mcq-edit-choices-header pb-3">
                <CardTitle className="mcq-edit-choices-title text-base">
                    {t('answerChoices')} ({data.choices.length})
                </CardTitle>
            </div>
            <ChoicesContainer
                single={!data.multipleCorrect}
                value={singleCorrectId}
                onValueChange={handleSingleCorrectChange}
                disabled={disabled}
            >
                {data.choices.map((choice, index) => (
                    <div
                        key={choice.id}
                        className="mcq-edit-choice-item bg-muted/30 border-0 shadow-sm py-2"
                    >
                        <div className="mcq-edit-choice-body px-2 [&:last-child]:pb-2">
                            {/* Choice Header */}
                            <div className="mcq-edit-choice-header flex items-center gap-2">
                                <GripVertical className="mcq-edit-choice-drag h-4 w-4 text-muted-foreground cursor-grab" />
                                <span className="mcq-edit-choice-label font-medium text-sm">
                                    {t('choiceLabel')} {String.fromCharCode(65 + index)}
                                </span>
                                <div className="flex-1" />
                                <div className="flex items-center gap-1">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={() => moveUp(index)}
                                        disabled={disabled || index === 0}
                                    >
                                        <ChevronUp className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={() => moveDown(index)}
                                        disabled={disabled || index === data.choices.length - 1}
                                    >
                                        <ChevronDown className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-destructive hover:text-destructive"
                                        onClick={() => removeChoice(choice.id)}
                                        disabled={disabled || data.choices.length <= 2}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            {/* Content Editor with oneline layout + correct checkbox */}
                            <div className="mcq-edit-choice-input flex items-center gap-2">
                                {data.multipleCorrect ? (
                                    <Checkbox
                                        id={`correct-${choice.id}`}
                                        checked={choice.isCorrect}
                                        onCheckedChange={(checked) =>
                                            updateChoice(choice.id, {
                                                isCorrect: checked === true,
                                            })
                                        }
                                        disabled={disabled}
                                        className="mcq-edit-choice-actions shrink-0"
                                        aria-label={t('correctAnswer')}
                                    />
                                ) : (
                                    <RadioGroupItem
                                        id={`correct-${choice.id}`}
                                        value={choice.id}
                                        disabled={disabled}
                                        className="mcq-edit-choice-actions mcq-edit-choice-radio shrink-0"
                                        aria-label={t('correctAnswer')}
                                    />
                                )}
                                <div className="flex-1 min-w-0">
                                    <BlockEditor
                                        layout="oneline"
                                        content={
                                            choice.content || {
                                                type: 'text' as const,
                                                text: '',
                                                variant: 'body' as const,
                                                alignment: 'left' as const,
                                            }
                                        }
                                        onChange={(content) =>
                                            updateChoice(choice.id, {
                                                content: content as MCQContent,
                                            })
                                        }
                                        onTypeChange={(type) =>
                                            handleChoiceTypeChange(choice.id, type)
                                        }
                                        disabled={disabled}
                                        placeholder={t('enterChoiceText')}
                                    />
                                </div>
                            </div>

                            {/* Feedback */}
                            {/* DISABLE FOR MATCHING WITH UI FIGMA */}
                            {/* <div className="mcq-edit-choice-feedback pt-2">
                                    <Label className="text-sm text-muted-foreground">
                                        Feedback (optional)
                                    </Label>
                                    <Input
                                        value={choice.feedback || ''}
                                        onChange={(e) =>
                                            updateChoice(choice.id, { feedback: e.target.value })
                                        }
                                        placeholder="Feedback for this choice..."
                                        disabled={disabled}
                                        className="mt-1"
                                    />
                                </div> */}
                        </div>
                    </div>
                ))}
                {correctAnswerError && (
                    <p className="mcq-edit-error text-sm text-destructive pt-1">
                        {correctAnswerError}
                    </p>
                )}
                <div className="mcq-edit-add-choice-row flex pt-3">
                    <Button
                        type="button"
                        size="default"
                        variant="default"
                        onClick={addChoice}
                        disabled={disabled || data.choices.length >= 8}
                        className="mcq-edit-add-choice-button font-semibold shadow-sm"
                    >
                        <Plus className="h-4 w-4 shrink-0" />
                        {t('addChoice')}
                    </Button>
                </div>
            </ChoicesContainer>

            <div className="mcq-edit-settings-body space-y-4 pt-4">
                {/* Explanation */}
                <div className="mcq-edit-explanation-field space-y-2">
                    <Label htmlFor="explanation">{t('explanation')}</Label>
                    <Textarea
                        id="explanation"
                        value={data.explanation || ''}
                        onChange={(e) => updateData({ explanation: e.target.value })}
                        placeholder={t('explainCorrectAnswer')}
                        disabled={disabled}
                    />
                </div>

                {/* Toggles */}
                <div className="mcq-edit-toggles flex flex-wrap gap-6">
                    <div className="flex items-center gap-2">
                        <Switch
                            id="multipleCorrect"
                            checked={data.multipleCorrect}
                            onCheckedChange={handleMultipleCorrectChange}
                            disabled={disabled}
                        />
                        <Label htmlFor="multipleCorrect">{t('multipleCorrectAnswers')}</Label>
                    </div>
                    <div className="flex items-center gap-2">
                        <Switch
                            id="shuffleChoices"
                            checked={data.shuffleChoices}
                            onCheckedChange={(checked) => updateData({ shuffleChoices: checked })}
                            disabled={disabled}
                        />
                        <Label htmlFor="shuffleChoices">{t('shuffleChoicesEditor')}</Label>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MCQEdit;
